<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="12" class="text-center text-sm-left flex-header">
        <h3 class="page-title">
          Klient
          <router-link :to="'/klienci/edycja/' + parseInt($route.params.client_id, 10)">
            <template v-if="client && client.type === 'corporation'">{{ client.company_name }}</template>
            <template v-else-if="client">{{ client.name }} {{ client.surname }}</template>
          </router-link>
          <template v-if="schedule && (isDraft || isForInternalClient)">
            <small class="ml-1">
              <template v-if="isDraft">
                &#8212; szkic umowy

                <span v-if="isForInternalClient"> wewnętrznej</span>:
                <span
                  v-if="schedule.contract_number"
                  class="text-primary">
                  {{ schedule.contract_number }}
                </span>
                <span v-else class="text-danger">
                  brak numeru umowy
                </span>
              </template>
              <template v-else>
                &#8212; umowa wewnętrzna
              </template>
            </small>
          </template>
        </h3>
        <ContractManagementBar
          :isDraft="isDraft"
          :isAdmin="isAdmin"
          :submitting="submitting"
          :savingAsDraft="savingAsDraft"
          :submittingContractToAccept="submittingContractToAccept"
          :schedule="schedule"
          @route-back="routeBack"
          @route-to-edit-client="routeToEditClient"
          @save-draft="submitForm(true)"
          @save-contract="submitForm(false)"
          @send-contract-to-accept="sendContractToAccept"
        />
      </d-col>
      <d-row v-if="isDraft && schedule.is_accepted === 2">
        <d-col cos sm="12">
          <small class="badge badge-pill badge-primary">Oczekuje na akceptację</small>
        </d-col>
      </d-row>
      <d-row>
        <d-col cos sm="12">
          <d-badge
            v-if="schedule.include_vat === 1"
            theme="success"
            class="mb-2"
          >Umowa z VAT</d-badge>
          <d-badge
            v-if="schedule.payment_allocation === 'interest_first'"
            theme="info"
            class="mb-2 ml-1"
          >Wpłaty: najpierw koszty i odsetki</d-badge>
        </d-col>
      </d-row>
    </d-row>
    <div v-if="!allDataLoaded">
      <h2 class="preloader isLoading">Trwa ładowanie</h2>
    </div>
    <d-card class="card-small">
      <d-form>
        <d-list-group
          flush
          @keyup.enter="submitForm"
          class="contract-view">
          <ul
            v-if="allDataLoaded"
            class="ss-tabs-nav">
            <li
              :class="{ active: activeTab === 'basicInfo' }"
              @click="activeTab = 'basicInfo'">
              <span>Dane podstawowe</span>
            </li>
            <li
              :class="{ active: activeTab === 'docs' }"
              @click="activeTab = 'docs'">
              <span>Faktury i dokumenty</span>
            </li>
            <li
              v-if="!isForInternalClient"
              :class="{ active: activeTab === 'guarantors' }"
              @click="activeTab = 'guarantors'">
              <span>Poręczyciele</span>
            </li>
            <li
              v-if="!isForInternalClient"
              :class="{ active: activeTab === 'guarantees' }"
              @click="activeTab = 'guarantees'">
              <span>Zabezpieczenia</span>
            </li>
            <li
              v-if="!isDraft"
              :class="{ active: activeTab === 'schedule' }"
              @click="activeTab = 'schedule'">
              <span>Harmonogram</span>
            </li>
            <li
              v-if="!isDraft"
              :class="{ active: activeTab === 'paymentHistory' }"
              @click="activeTab = 'paymentHistory'">
              <span>Historia płatności</span>
            </li>
            <li
              v-if="!isDraft && !isForInternalClient"
              :class="{ active: activeTab === 'paymentRequests' }"
              @click="activeTab = 'paymentRequests'">
              <span>Wezwania do zapłaty</span>
            </li>
            <li
              v-if="!isDraft && !isForInternalClient"
              :class="{ active: activeTab === 'contactHistory' }"
              @click="activeTab = 'contactHistory'">
              <span>Historia kontaktu</span>
            </li>
            <li
              v-if="schedule.case_is_explained !== '1' && !isDraft && !isForInternalClient"
              class="disabled">
              <ss-popover>
                <span>Sprawy egzekucyjne</span>
                <template slot="tooltip">
                  Zakładka dostępna gdy umowa jest w egzekucji
                </template>
              </ss-popover>
            </li>
            <li
              v-if="schedule.case_is_explained === '1' && !isDraft && !isForInternalClient"
              :class="{ active: activeTab === 'executions' }"
              @click="activeTab = 'executions'">
              <span>Sprawy egzekucyje</span>
            </li>
            <li
              v-if="!isForInternalClient"
              :class="{
                  active: activeTab === 'launchInstructions',
                  'has-error': schedule.provision_instructions_added !== true || schedule.disposition_instructions_added !== true
              }"
              @click="activeTab = 'launchInstructions'">
              <span>Dyspozycje uruchomienia</span>
            </li>
            <li
              v-if="!isForInternalClient"
              :class="{ active: activeTab === 'contractDocs' }"
              @click="activeTab = 'contractDocs'">
              <span>Druki umowy</span>
            </li>
            <li
              v-if="!isForInternalClient"
              :class="{ active: activeTab === 'contractConditions' }"
              @click="activeTab = 'contractConditions'">
              <span>Warunki umowy</span>
            </li>
            <li
              v-if="schedule.overpayment > 0 || overPaymentSettlements.length"
              :class="{
                active: activeTab === 'contractOverpayment',
                'has-error': ((schedule.overpayment > 0 && (overpaymentSettlementsSum < schedule.overpayment)) || (overpaymentSettlementsSum > schedule.overpayment))
              }"
              @click="activeTab = 'contractOverpayment'">
              <span>Rozbicie nadpłaty</span>
            </li>
            <li
              v-if="!isForInternalClient"
              :class="{ active: activeTab === 'contractExpenses' }"
              @click="activeTab = 'contractExpenses'">
              <span>Wydatki umowy</span>
            </li>
            <li
              :class="{ active: activeTab === 'discounts' }"
              @click="activeTab = 'discounts'">
              <span>Bonifikaty</span>
            </li>
          </ul>

          <ul
            v-if="allDataLoaded"
            class="ss-tabs">
            <li
              v-if="activeTab === 'basicInfo'"
              class="active">
              <contract-basic-info
                :all-companies="allCompanies"
                :all-guarantees-have-agreement="allGuaranteesHaveAgreement"
                :client-name="clientName"
                :client-type="client.type"
                :export-in-progress="exporting_pdf"
                :interests-loading="interests_loading"
                :is-admin="isAdmin"
                :is-draft="isDraft"
                :is-for-internal-client="isForInternalClient"
                :last-installment="lastInstallment"
                :schedule="schedule"
                :schedule-finished="scheduleFinished" />
            </li>
            <li
              v-if="activeTab === 'docs'"
              class="active">
              <contract-invoices-and-docs
                :client-name="clientName"
                :documents="documents"
                :is-admin="isAdmin"
                :is-draft="isDraft"
                :schedule="schedule"
                :schedule-finished="scheduleFinished" />
            </li>
            <li
              v-show="activeTab === 'guarantors'"
              class="active">
              <contract-guarantors
                ref="contract-guarantors"
                :guarantors-list="guarantors"
                :is-draft="isDraft"
                :schedule-guarantors-list="schedule.guarantors"
                :client-id="client && client.id ? client.id : null" />
            </li>
            <li
              v-show="activeTab === 'guarantees'"
              class="active">
              <contract-guarantees
                ref="contract-guarantees"
                :client-id="client.id"
                :client-name="clientName"
                :guarantees="guarantees"
                :guarantees-default="schedule.payment_guarantees"
                :guarantors="guarantors"
                :is-draft="isDraft"
                :schedule="schedule"/>
            </li>
            <li
              v-if="activeTab === 'schedule'"
              class="active">
              <contract-schedule
                :export-in-progress="exporting_pdf"
                :interests-loading="interests_loading"
                :is-changed-installments="isChangedInstallments"
                :is-draft="isDraft"
                :last-installment="lastInstallment"
                :schedule="schedule" />
            </li>
            <li
              v-show="activeTab === 'paymentHistory'"
              class="active">
              <d-card-header class="border-bottom flex-header">
                <h6 class="m-0">Historia płatności</h6>
              </d-card-header>
              <payments-history :finished="scheduleFinished" />
            </li>
            <li
              v-show="activeTab === 'paymentRequests'"
              class="active">
              <contract-payment-requests
                :reminders="reminders"
                :schedule-finished="scheduleFinished"
                :schedule-prop="schedule" />
            </li>
            <li
              v-if="activeTab === 'contactHistory'"
              class="active">
              <contract-contact-history-tab
                :contact-history="contactHistory"
                :schedule-finished="scheduleFinished"
                :schedule-id="schedule.id"
                :client-id="client && client.id? client.id : null" />
            </li>
            <li
              v-if="activeTab === 'executions'"
              class="active">
              <execution-view
                :guarantors="guarantors"
                :client="client" />
            </li>
            <li
              v-if="activeTab === 'launchInstructions'"
              class="active">
              <contract-instructions-tab
                :client="client"
                :company-id="companyIdInitialVal"
                :instructions-list="instructionsList"
                :is-draft="isDraft"
                :schedule="schedule"
                :schedule-finished="scheduleFinished"/>
            </li>
            <li
              v-show="activeTab === 'contractDocs'"
              class="active" >
              <contract-docs
                :company-id.sync="companyIdInitialVal"
                :company-extra-text="schedule.company_extra_text_default"
                :guarantee-docs="schedule.guarantee_doc"
                :contract-docs="schedule.contract_doc"
                :contract-date="schedule.contract_date" />
            </li>
            <li
              v-if="activeTab === 'contractConditions'"
              class="active" >
              <contract-conditions />
            </li>
            <li
              v-if="(schedule.overpayment > 0 || (overPaymentSettlements && overPaymentSettlements.length)) && (activeTab === 'contractOverpayment')"
              class="active">
              <contract-overpayment
                :overpayment="schedule.overpayment"
                :schedule-id="schedule.id"
                :overpayment-settlements="overPaymentSettlements"
                :overpayment-settlements-loaded="overPaymentSettlementsLoaded"
                :overpayment-settlements-sum="overpaymentSettlementsSum" />
            </li>
            <li
              v-if="activeTab === 'contractExpenses'"
              class="active" >
              <ContractExpensesTab />
            </li>
            <li
              v-if="activeTab === 'discounts'"
              class="active" >
              <ContractDiscounts
                :discounts-prop="schedule.discounts"
                @update-discounts="updateDiscounts" />
            </li>
          </ul>
        </d-list-group>
      </d-form>
    </d-card>
  </d-container>
</template>

<script>
import Vue from 'vue';
import FileSaver from 'file-saver';
import ContractBasicInfo from '@/components/client/contract-partials/ContractBasicInfo.vue';
import ContractConditions from './ContractConditions';
import ContractContactHistoryTab from '@/components/client/contract-partials/ContractContactHistoryTab.vue';
import ContractDocs from '@/components/client/ContractDocs.vue';
import ContractGuarantees from '@/components/client/contract-partials/guarantees/ContractGuarantees.vue';
import ContractGuarantors from '@/components/client/contract-partials/guarantors/ContractGuarantors';
import ContractInstructionsTab from '@//components/client/contract-partials/instructions/ContractInstructionsTab.vue';
import ContractInvoicesAndDocs from '@/components/client/contract-partials/docs/ContractInvoicesAndDocs';
import ContractOverpayment from '@/components/client/contract-partials/ContractOverpayment';
import ContractPaymentRequests from '@/components/client/contract-partials/payment-requests/ContractPaymentRequests.vue';
import ContractSchedule from '@/components/client/contract-partials/contract-schedule/ContractSchedule';
import ExecutionView from '../execution/ExecutionView';
import LarErrors from '@/helpers/Errors';
import { capitalParts } from '@/utils/scheduleCapitals';
import lodash from 'lodash';
import PaymentsHistory from '@/views/PaymentsHistory';
import SsDate from '@/components/common/SsDate';
import SsPriceInput from '@/components/common/SsPriceInput';
import SsTextarea from '@/components/common/SsTextarea';
import { mapGetters, mapActions } from 'vuex';
import ClientUtils from '@/utils/client';
import FormUtils from '@/utils/FormUtils';
import CButton from '@/components/contracts/edit/atoms/CButton';
import ContractManagementBar from '@/components/contracts/edit/molecules/ContractManagementBar';
import ContractExpensesTab from '@/components/client/ContractExpensesTab';
import ContractDiscounts from '@/components/client/contract-partials/discounts/ContractDiscounts';
// import ContractTabManagementList from '../contracts/edit/molecules/ContractTabManagementList.vue';


export default {
  name: 'ContractEdit',
  components: {
    ContractBasicInfo,
    ContractConditions,
    ContractContactHistoryTab,
    ContractDocs,
    ContractGuarantees,
    ContractGuarantors,
    ContractInstructionsTab,
    ContractInvoicesAndDocs,
    ContractOverpayment,
    ContractPaymentRequests,
    ContractSchedule,
    ExecutionView,
    LarErrors,
    lodash,
    PaymentsHistory,
    SsDate,
    SsPriceInput,
    SsTextarea,
    CButton,
    ContractManagementBar,
    ContractExpensesTab,
    ContractDiscounts
    // ContractTabManagementList
},
  computed: {
    ...mapGetters([
      'allCompanies',
      'getUser'
    ]),
    allDataLoaded () {
      return this.scheduleLoaded &&  this.remindersLoaded && this.guaranteesLoaded && this.guarantorsLoaded && this.documentsLoaded && this.contactHistoryLoaded && this.instructionsListLoaded && this.clientFetched;
    },
    isForInternalClient () {
      if (!this.client || !this.client.id) {
        return false;
      }
      return !!Number(this.client.client_internal);
    },
    isDraft () {
      return this.schedule.draft === 1;
    },
    clientName () {
      return this.client.type === 'corporation' ?  this.client.company_name : this.client.name  + '_' + this.client.surname;
    },
    allGuaranteesHaveAgreement () {
      let scheduleGuarantees = this.guarantees.filter(guarantee => this.schedule.payment_guarantees.some(item => item.id === guarantee.id));

      return scheduleGuarantees.length === 0 || (scheduleGuarantees.length > 0 && scheduleGuarantees.reduce((total, guarantee) => {return total + (guarantee.status !== 'excerpt_received' && guarantee.status !== 'released' ? 1 : 0)}, 0) === 0);
    },
    installments_current () {
      return this.schedule.installments.map((inst)=>{return parseFloat(inst.installment_fee).toFixed(2)});
    },
    scheduleFinished () {
      return this.schedule.status === 'archived' || this.schedule.status === 'closed';
    },
    isChangedInstallments () {
      let a1 = this.installments;
      let a2 = this.installments_current;
      return JSON.stringify(a1) !== JSON.stringify(a2);
    },
    capitalSum () {
      return this.schedule.loan_amount + this.schedule.installment_sum;
    },
    lastInstallment () {
      if (!this.schedule.installments || !this.schedule.installments.length) {
        return 0;
      }

      let interestSum = this.schedule.interest_sum;
      let penaltySum = this.schedule.interest_penalty_sum;
      let remindersSum = this.schedule.additional_costs;

      if (this.isDraft) {
        remindersSum = Number(this.schedule.default_reminder_price);
        interestSum = this.schedule.installments.reduce((sum, item) => sum + item.interest, 0);
      }

      return this.schedule.installments[this.schedule.installments.length - 1].installment_fee +
      interestSum + penaltySum + remindersSum;
    },
    installmentsTrigger () {
      return this.schedule.interest_rate + '=' + this.schedule.installments.map(installment => [installment.installment_fee, installment.payment_date, installment.capital].join('-')).join(';');
    },
    overpaymentSettlementsSum () {
      if (!this.overPaymentSettlementsLoaded || !this.overPaymentSettlements || !this.overPaymentSettlements.length) {
        return 0;
      }

      return this.overPaymentSettlements.reduce((prev, item) => prev + parseFloat(item.amount), 0);
    },
    isAdmin () {
      return this.getUser.is_admin;
    }
  },
  watch: {
    'schedule.interest_rate' () {
      this.calculateCapitals();
    },
    'schedule.payment_allocation' () {
      this.calculateCapitals();
      this.getInterests();
    },
    'schedule.loan_amount' () {
      this.calculateCapitals();
    },
    'schedule.installment_sum'(){
      this.calculateCapitals();
    },
    scheduleSum (to){
      this.schedule.interest_sum = to;
    },
    capitalSum (to) {
      this.schedule.loan_installment_sum = to;
    },
    installmentsTrigger () {
      this.getInterests();
    },
    allDataLoaded (newVal, oldVal) {
      if (!oldVal && newVal && this.$route.params.case_id) {
        Vue.set(this, 'activeTab', 'executions');
        this.$nextTick(() => {
          this.$bus.$emit('execution-edit', this.$route.params.case_id);
        });
      }
    }
  },
  data () {
    return {
      activeTab: 'basicInfo',
      client: null,
      clientFetched: false,
      companyIdInitialVal: null,
      contact: _.clone(ClientUtils._contact),
      contactHistory: [],
      contactHistoryLoaded: false,
      documents: [],
      documentsLoaded: false,
      exporting_pdf: false,
      guarantee: {},
      guarantees: [],
      guaranteesLoaded: false,
      guarantor: null,
      guarantors: [],
      guarantorsLoaded: false,
      installments: [],
      instructions: _.clone(ClientUtils._instructions),
      instructionsList: [],
      instructionsListLoaded: false,
      interests_loading: false,
      lar_errors: LarErrors,
      overPaymentSettlements: [],
      overPaymentSettlementsLoaded: false,
      reminders: [],
      remindersLoaded: false,
      savingAsDraft: false,
      schedule: _.clone(ClientUtils._schedule),
      scheduleLoaded: false,
      submitting: false,
      submittingContractToAccept: false
    }
  },
  mounted () {
    this.$bus.$on('reload-contract', this.fetchData);
    this.$bus.$on('reload-overpayment-settlements', this.loadOverpaymentSettlements);
    this.$bus.$on('contract-calculate-capitals', this.calculateCapitals);
    this.$bus.$on('contract-schedule-export-pdf', this.exportToPdf);
    this.$bus.$on('contract-schedule-deactivate-discount', this.deactivateDiscount);
    this.$bus.$on('contract-get-interests', this.getInterests);
    this.$bus.$on('contract-reload-documents', this.loadDocuments);
    this.$bus.$on('contract-reload-guarantors', this.loadGuarantors);
    this.$bus.$on('contract-reload-contact-history', this.loadContactHistory);
    this.$bus.$on('contract-reload-instructions', this.loadInstructions);
    this.$bus.$on('contract-reload-guarantees', this.loadGuarantees);
    this.$bus.$on('contract-reload-reminders', this.getReminders);

    if (!this.$route.params.client) {
      this.loadClient();
    } else {
      this.client = this.$route.params.client;
      this.clientFetched = true;
    }

    this.fetchData();
    this.loadContactHistory();
    this.getAllCompanies();
    this.getReminders();
    this.loadGuarantees();
    this.loadInstructions();
    this.loadDocuments();
    this.loadOverpaymentSettlements();
  },
  methods: {
    routeBack(){
      return this.$router.push({ name: this.$route.params.from })
    },
    routeToEditClient(){
      return this.$router.push('/klienci/edycja/' + parseInt(this.$route.params.client_id, 10))
    },
    handleTabChange(selectedTab){
      this.activeTab = selectedTab
    },
    ...mapActions([
      'getAllCompanies'
    ]),
    getReminders () {
      Vue.set(this, 'remindersLoaded', false);

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/contract-reminders/get-reminders',
        method: 'post',
        responseKey: 'reminders',
        outputKey: 'reminders',
        loadedKey: 'remindersLoaded',
        errorKey: 'loadError',
        noPagination: true,
        params: {
          schedule_id: this.$route.params.schedule_id,
        }
      });
    },
    loadGuarantees () {
      Vue.set(this, 'guaranteesLoaded', false);

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/payment-guarantees/get-payment-guarantees',
        method: 'post',
        responseKey: 'payment_guarantees',
        outputKey: 'guarantees',
        loadedKey: 'guaranteesLoaded',
        errorKey: 'loadError',
        noPagination: true,
        booleanFields: ['is_burdens_on_property'],
        params: {
          client_id: this.$route.params.client_id,
          schedule_id: this.$route.params.schedule_id,
        },
      });
    },
    loadDocuments (isNew) {
      Vue.set(this, 'documentsLoaded', false);

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/clients/get-documents',
        method: 'post',
        responseKey: 'documents',
        outputKey: 'documents',
        loadedKey: 'documentsLoaded',
        errorKey: 'loadError',
        noPagination: true,
        params: {
          client_id: this.$route.params.client_id,
          schedule_id: this.$route.params.schedule_id
        },
        successAction: () => {
          if (isNew){
            this.schedule.documents.push(this.documents[this.documents.length - 1].id);
          }
        }
      });
    },
    loadGuarantors () {
      Vue.set(this, 'guarantorsLoaded', false);

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/guarantors/get-guarantors',
        method: 'post',
        responseKey: 'guarantors',
        outputKey: 'guarantors',
        loadedKey: 'guarantorsLoaded',
        errorKey: 'loadError',
        noPagination: true,
        params: {
          client_id: this.$route.params.client_id,
          schedule_id: this.$route.params.schedule_id
        },
        successAction: () => {
          let mergedGuarantors = Vue.mergeLists(this.guarantors, this.schedule.guarantors, 'id').map( guarantor => {
            guarantor.declaration_amount = guarantor.declaration_amount ? guarantor.declaration_amount : '0';
            guarantor.declaration_date = guarantor.declaration_date ? guarantor.declaration_date : '';
            guarantor.declaration_doc = guarantor.declaration_doc ? guarantor.declaration_doc : '';
            return guarantor;
          }, []);

          Vue.set(this, 'guarantors', mergedGuarantors);
        }
      });
    },
    submitForm (saveAsDraft = false) {
      if (this.schedule.installments[this.schedule.installments.length - 1].capital < 0) {
        Vue.swal({
          type: 'warning',
          title: 'Kapitał rat nie może być ujemny.',
          showConfirmButton: true,
        });
      } else if (this.isDraft && !saveAsDraft) {
        let message = 'Czy potwierdzasz przekształcenie w umowę?';

        if (!this.isForInternalClient && this.$refs['contract-guarantors'] && this.$refs['contract-guarantors'].scheduleGuarantors.length < 1) {
          message += '<br />Nie został wybrany żaden poręczyciel.'
        }

        if (!this.isForInternalClient && this.$refs['contract-guarantees'] && this.$refs['contract-guarantees'].scheduleGuarantees.length < 1) {
          message += '<br />Nie zostało wybrane żadne zabezpieczenie.'
        }
        Vue.swal({
          title: 'Uwaga',
          html: message,
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Tak',
          cancelButtonText: 'Nie',
          buttonsStyling: true
        }).then((result) => {
          if (result.value) {
            this.submitFormConfirm(saveAsDraft);
          } else {
            this.submitting = false;
          }
        });
      } else {
        this.submitFormConfirm(saveAsDraft)
      }
    },
    submitFormConfirm (saveAsDraft = false) {
      this.savingAsDraft = saveAsDraft;
      this.submitting = true;
      this.schedule.loan_installment_sum = this.capitalSum;
      this.schedule.installment_sum= this.capitalSum - this.schedule.loan_amount;
      this.schedule.guarantors = [];
      this.schedule.payment_guarantees = [];

      if (this.$refs['contract-guarantors']) {
        this.schedule.guarantors = this.$refs['contract-guarantors'].selectedGuarantorsFormatted;
      }

      if (this.$refs['contract-guarantees']) {
        this.schedule.payment_guarantees = this.$refs['contract-guarantees'].selectedGuaranteesFormatted;
      }

      let data = Object.assign({}, this.schedule);
      if(saveAsDraft == false){
        data.draft = 0;
      }

      axios.post('api/schedules/save-schedule', data).then(response => {
        this.submitting = false;
        this.savingAsDraft = false;
        Vue.swal({
          type: 'success',
          title: 'Umowa została zapisana.',
          showConfirmButton: true,
        }).then(() => {
          this.fetchData();
        });
      }).catch((error) => {
        Vue.swal({
          type: 'error',
          title: error.response.data.message,
          html: Vue.transformErrorsToHtml(error.response.data.errors),
          showConfirmButton: true,
        });
        this.submitting = false;
        this.savingAsDraft = false;
        LarErrors.record(error.response.data.errors);
      })
    },
    calculateCapitals () {
      let capital = parseFloat(this.capitalSum);
      let sum = 0;
      let installments = _.clone(this.schedule.installments);
      if (installments) {
        // interest_first: z raty schodzi na kapitał tylko część ponad odsetki okresu
        let parts = capitalParts(installments, this.schedule.payment_allocation);
        for(let i = 0; installments.length > i; i++){
          installments[i].capital = capital - sum;

          if(i === this.schedule.number_of_installments - 1 ){
            installments[i].installment_fee = capital - sum;
          }
          sum += parts[i];
        }
      }
      this.schedule.installments = installments;
      this.$forceUpdate();
    },
    fetchData () {
      this.$set(this, 'scheduleLoaded', false);

      axios.post('api/schedules/get-schedule', {id: this.$route.params.schedule_id}).then(response => {
        this.schedule = response.data;

        this.installments = [];
        for (let inst of response.data.installments) {
          this.installments.push(parseFloat(inst.installment_fee).toFixed(2));
        }

        this.$set(this, 'companyIdInitialVal', this.schedule.company_id);
        if (this.schedule.case_is_explained) {
          this.schedule.case_is_explained = this.schedule.case_is_explained;
        }

        this.rebuildSchedule();

        this.$eventHub.$emit('refresh-payments-history');
        this.$set(this, 'companyIdInitialVal', response.data.company_id);
        this.$set(this, 'scheduleLoaded', true);

        this.loadGuarantors();
      }).catch(errors => {
        console.log(errors);
        this.$set(this, 'scheduleLoaded', true);
      })
    },
    rebuildSchedule () {
      if (!this.schedule.payment_guarantees) {
        this.schedule.payment_guarantees = [];
      }
      if (!this.schedule.guarantors) {
        this.schedule.guarantors = [];
      }
      if (!this.schedule.documents) {
        this.schedule.documents = [];
      }
      this.installments = [];
      if (this.schedule.installments){
        for (let inst of this.schedule.installments) {
          this.installments.push(parseFloat(inst.installment_fee).toFixed(2));
        }
      }
    },
    sendContractToAccept () {
      this.submittingContractToAccept = true;
      axios.post('api/schedules/accept-schedule', { id: this.schedule.id }
      ).then(response => {
        Vue.swal({
          type: 'success',
          title: 'Umowa została wysłana do akceptacji.',
          showConfirmButton: true,
        }).then(() => {
          this.schedule.is_accepted = 2;
          this.fetchData();
        });
        this.submittingContractToAccept = false;
      }).catch((error) => {
        Vue.swal({
          type: 'warning',
          title: error.response.data.message,
          html: Vue.transformErrorsToHtml(error.response.data.errors),
          showConfirmButton: true,
        })
        LarErrors.record(error.response.data.errors);
        this.submittingContractToAccept = false;
      })
    },
    getInterests: _.debounce(function () {
      this.interests_loading = true;

      let data = {
        'installments': this.schedule.installments.map(installment => ({
          'installment_fee': installment.installment_fee,
          'payment_date': installment.payment_date,
          'capital': installment.capital
        })),
        'interest_rate': this.schedule.interest_rate,
        'include_vat': this.schedule.include_vat,
        'payment_allocation': this.schedule.payment_allocation
      };

      if (this.isDraft) {
        data.contract_date = this.schedule.contract_date
      } else {
        data.schedule_id = this.schedule.id
      }

      axios.post('api/calculation/get-interests', data).then(response => {
        for (let i = 0; i < response.data.installments.length; i++) {
          this.schedule.installments[i].interest = response.data.installments[i].interest;
        }
        this.interests_loading = false;
        this.calculateCapitals()
      }).catch((error) => {
        this.interests_loading = false;
        console.log(error);
        LarErrors.record(error.response.data.errors)
      })
    }, 500),
    loadContactHistory () {
      Vue.set(this, 'contactHistoryLoaded', false);

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/contact-history/get-client-history',
        method: 'post',
        responseKey: 'contact_history',
        outputKey: 'contactHistory',
        loadedKey: 'contactHistoryLoaded',
        errorKey: 'loadError',
        noPagination: true,
        params: {
          client_id: this.$route.params.client_id,
          schedule_id: this.$route.params.schedule_id
        }
      });
    },
    loadInstructions () {
      Vue.set(this, 'instructionsListLoaded', false);

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/contract-instructions/items',
        method: 'post',
        responseKey: 'data',
        outputKey: 'instructionsList',
        loadedKey: 'instructionsListLoaded',
        errorKey: 'loadError',
        noPagination: true,
        params: {
          where: {
            client_id: this.$route.params.client_id,
            schedule_id: this.$route.params.schedule_id
          }
        }
      });
    },
    loadOverpaymentSettlements () {
      Vue.set(this, 'overPaymentSettlementsLoaded', false);
      FormUtils.loadAdditionalData(this, {
        method: 'post',
        endpoint: '/api/overpayment-settlement/items',
        outputKey: 'overPaymentSettlements',
        loadedKey: 'overPaymentSettlementsLoaded',
        params: {
          where: {
            schedule_id: this.$route.params.schedule_id
          }
        }
      });
    },
    exportToPdf () {
      this.exporting_pdf = true;
      let file_name = this.clientName + '_harmonogram_' +  Vue.getCurrentDate() + '.pdf';
      if(this.schedule.schedule_attachment_name != undefined && this.schedule.schedule_attachment_name.length > 0){
          file_name = this.schedule.schedule_attachment_name + '.pdf';
      }
      axios.post('api/schedules/generate-pdf', { schedule_id: this.schedule.id },
        {responseType: 'blob'}).then(response => {
        let blob = new File(
        [response.data],
        file_name,
        { type: 'application/pdf' }
        );
        FileSaver.saveAs(blob);
        this.exporting_pdf = false;
      }).catch( error => {
        this.exporting_pdf = false;
      })
    },
    deactivateDiscount () {
      Vue.swal({
        title: 'Uwaga',
        html: 'Czy jesteś pewny, że chcesz wycofać bonifikatę?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak',
        cancelButtonText: 'Nie',
        buttonsStyling: true
      }).then((result) => {
        if (result.value) {
          let currentActiveIndex = this.schedule.discounts.findIndex(item => item.discount_active);

          if (currentActiveIndex < 0) {
            console.warn('no active discount found');
            return;
          }
          this.schedule.discounts[currentActiveIndex].discount_active = false;
          this.submitForm();
        }
      });
    },
    loadClient () {
      axios.post('api/clients/get-client', {
        id: this.$route.params.client_id
      }).then(response => {
        this.client = response.data.data;

        if (typeof this.client.is_second_person === 'number') {
          this.client.is_second_person = this.client.is_second_person.toString();
        }

        if (this.client.is_second_person === '') {
          this.client.is_second_person = '0';
        }

        if (typeof this.client.client_internal === 'number') {
          this.client.client_internal = this.client.client_internal.toString();
        }

        if (this.client.client_internal === '') {
          this.client.client_internal = '0';
        }

        this.clientFetched = true;
      }).catch(errors => {
        console.log(errors);
        this.clientFetched = true;
      })
    },
    updateDiscounts (newDiscounts) {
      Vue.set(this.schedule, 'discounts', newDiscounts);
    }
  },
  beforeDestroy () {
    this.$bus.$off('reload-contract', this.fetchData);
    this.$bus.$off('reload-overpayment-settlements', this.loadOverpaymentSettlements);
    this.$bus.$off('contract-calculate-capitals', this.calculateCapitals);
    this.$bus.$off('contract-schedule-export-pdf', this.exportToPdf);
    this.$bus.$off('contract-schedule-deactivate-discount', this.deactivateDiscount);
    this.$bus.$off('contract-get-interests', this.getInterests);
    this.$bus.$off('contract-reload-documents', this.loadDocuments);
    this.$bus.$off('contract-reload-guarantors', this.loadGuarantors);
    this.$bus.$off('contract-reload-contact-history', this.loadContactHistory);
    this.$bus.$off('contract-reload-instructions', this.loadInstructions);
    this.$bus.$off('contract-reload-guarantees', this.loadGuarantees);
    this.$bus.$off('contract-reload-reminders', this.getReminders);
  }
}
</script>

<style lang="scss">
.contract-view {
  .contract-info .date-cell-wrapper,
  .date-cell-wrapper.date-button{
    position: relative;
    top: -6px;
    margin-left: 10px;
    display: inline-block;
  }
  .button-date-wrapper .ss-date .form-control{
    max-width: 100%;
  }

  td.is-loading {
    opacity: .5;
  }

  .date-cell-wrapper.date-button .ss-date,
  .table-schedule .date-cell-wrapper .ss-date {
    position: absolute;
    z-index: 999;
    top: -2px;
    left: 0;
    right: 0;
    bottom: -2px;
  }

  .popover-wrapper{
    position: relative;
    display: inline-block;

    &.center{
      .popover{
        left: 50%;
        -webkit-transform: translate(-50%, 0);
        -moz-transform: translate(-50%, 0);
        -ms-transform: translate(-50%, 0);
        -o-transform: translate(-50%, 0);
        transform: translate(-50%, 0);
        min-width: 200px;
      }
    }

    .popover{
      opacity: 0;
      pointer-events: none;
    }

    &:hover .popover{
      opacity: 1;
    }
  }
}
</style>
<style>
  .text-left > .form-control{
    text-align: left !important;
  }
</style>
